import { useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import classNames from "classnames";
import { Button } from "../Button";
import { useTranslation } from "react-i18next";

import Share from "../Share";
import SendTipModal from "../SendTipModal";
import LikesListModal from "../LikesListModal";
import { likePost, unlikePost } from "@redux/posts/actions";
import { ReactComponent as IconLike } from "../../assets/ic-like.svg";
import { ReactComponent as IconLiked } from "../../assets/ic-like-active.svg";
import { ReactComponent as IconComment } from "../../assets/ic-comment.svg";
import { useService } from "@/base/hooks/useService";
import { StorageService } from "@services/StorageService";
import { localstorageKeys } from "@const/store";

import styles from "./index.module.scss";
import { toggleSignInModal } from "@redux/signIn/actions";
import routes from "../../const/routes";

const PostFooter = ({
  postId,
  uniqueKey,
  likesCount,
  isLiked,
  commentsCount,
  className,
  username,
  isLikeActionAvailable,
  isCommentActionAvailable,
  hideDonate,
  commentButtonDisable,
  isSupporter,
  isMostProfitableSupporter,
  isPromoter,
  isMostProfitablePromoter,
  creatorId,
  isDeleted,
  counterClassName,
  counterSectionClassName,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation("common");
  const { t: t1 } = useTranslation("creator-profile");

  const [isSendTipModalOpen, setIsSendTipModalOpen] = useState(false);

  const [isLikersListOpen, setIsLikersListOpen] = useState(false);

  const storageService = useService(StorageService);
  const iconClassNames = classNames("me-1", styles.footerIcon);
  const buttonClassNames = classNames(
    "d-flex align-items-center text-body-1 text-regular",
    styles.button,
  );

  const { accessToken } = storageService.get(localstorageKeys.session, {});

  const { username: myUsername } = useSelector(
    ({ profileReducer }) => profileReducer,
  );

  const isMyPost = myUsername === username;

  const toggleLikersList = (e) => {
    e.stopPropagation();
    if (!isLikeActionAvailable) return;
    setIsLikersListOpen((state) => !state);
  };

  const toggleSendTipModalOpen = (e) => {
    e?.stopPropagation();
    if (!accessToken) {
      dispatch(toggleSignInModal(true));
      storageService.set(
        localstorageKeys.destination,
        `${routes.CREATOR_PROFILE()}${username}`,
      );
      return;
    }

    setIsSendTipModalOpen((prevState) => !prevState);
  };

  const likesCountCaption = likesCount === 1 ? "LIKE" : "LIKES";
  const commentsCountCaption = commentsCount === 1 ? "COMMENT" : "COMMENTS";

  const message = encodeURI(t("CHECK_MY_POST"));
  const messageWithUsername = encodeURI(t("CHECK_POST", { username }));

  const redirectToSignIn = () => {
    storageService.set(
      localstorageKeys.destination,
      `${routes.CREATOR_PROFILE()}${username}`,
    );
    dispatch(toggleSignInModal(true));
  };

  const handleLike = useCallback(
    () => dispatch(likePost({ postId, uniqueKey, isMyPost, creatorId })),
    [postId, uniqueKey, creatorId],
  );

  const handleUnlike = useCallback(
    () => dispatch(unlikePost({ postId, uniqueKey, isMyPost, creatorId })),
    [postId, uniqueKey, creatorId],
  );

  const handleLikeClick = useCallback(
    (e) => {
      e.stopPropagation();
      if (!accessToken) {
        return redirectToSignIn();
      }

      if (isLiked) {
        handleUnlike();
      } else {
        handleLike();
      }
    },
    [accessToken, isLiked, handleUnlike, handleLike],
  );

  const isLikeDisabled = accessToken ? !isLikeActionAvailable : false;

  return (
    <>
      <SendTipModal
        toggle={toggleSendTipModalOpen}
        isOpen={isSendTipModalOpen}
        creatorId={creatorId}
        isSupporter={isSupporter}
        isMostProfitableSupporter={isMostProfitableSupporter}
        isPromoter={isPromoter}
        isMostProfitablePromoter={isMostProfitablePromoter}
        postId={postId}
      />
      <div
        className={classNames(
          "d-flex align-items-center flex-wrap",
          styles.footer,
          className,
        )}
      >
        {(!!likesCount || !!commentsCount) && (
          <div
            className={classNames(
              "text-body-2 text-tertiary w-100",
              styles.counterSection,
              counterSectionClassName,
            )}
          >
            {!!likesCount && (
              <div
                onClick={toggleLikersList}
                className={classNames(
                  "cursor-pointer",
                  styles.counter,
                  !isLikeActionAvailable
                    ? "pointer-events-none"
                    : styles.counterHover,
                )}
              >
                <span className="text-body-2 text-regular me-1">
                  {likesCount}
                </span>
                <span className="text-tertiary text-body-2 text-regular">
                  {t(likesCountCaption)}
                </span>
              </div>
            )}
            {!!commentsCount && (
              <div
                className={classNames(
                  !commentButtonDisable && "cursor-pointer",
                  styles.counterHover,
                  commentButtonDisable && styles.countCommentsDisabled,
                )}
              >
                <span className="text-tertiary text-body-2 text-regular me-1">
                  {commentsCount}
                </span>
                <span className="text-tertiary text-body-2 text-regular">
                  {t(commentsCountCaption)}
                </span>
              </div>
            )}
          </div>
        )}
        <div className="d-flex justify-content-between w-100">
          <div className="d-flex">
            <div className="d-flex align-items-center me-4">
              <Button
                onClick={handleLikeClick}
                disabled={isLikeDisabled}
                className={classNames("btn-resetStyle", buttonClassNames)}
              >
                {isLiked ? (
                  <IconLiked
                    className={classNames(
                      iconClassNames,
                      styles.iconActive,
                      "text-primary",
                    )}
                  />
                ) : (
                  <IconLike className={iconClassNames} />
                )}
              </Button>
              <span
                className={classNames(
                  "text-tertiary text-body-2",
                  styles.counterMobile,
                  counterClassName,
                )}
              >
                {likesCount}
              </span>
            </div>
            <div className="d-flex align-items-center">
              <Button
                className={classNames("btn-resetStyle", buttonClassNames)}
                disabled={!isCommentActionAvailable || commentButtonDisable}
              >
                <IconComment className={iconClassNames} />
              </Button>
              <span
                className={classNames(
                  "text-tertiary text-body-2",
                  styles.counterMobile,
                  counterClassName,
                )}
              >
                {commentsCount}
              </span>
            </div>

            <Share
              withBody
              type="post"
              isMyProfile={isMyPost}
              postUniqueKey={uniqueKey}
              username={username}
              message={isMyPost ? message : messageWithUsername}
              togglerClassName={classNames(
                "btn-resetStyle",
                buttonClassNames,
                styles.share,
              )}
            />
          </div>
          {!isMyPost && !isDeleted && (
            <div className={classNames(hideDonate && "d-none", styles.tip)}>
              <Button
                color="secondary"
                size="sm"
                onClick={toggleSendTipModalOpen}
                className={styles.donateBtn}
              >
                <span className="text-primary">{t1("TIP")}</span>
              </Button>
            </div>
          )}
        </div>
      </div>
      <LikesListModal
        id={postId}
        uniqueKey={uniqueKey}
        isOpen={isLikersListOpen}
        onClose={toggleLikersList}
        isMyPost={isMyPost}
      />
    </>
  );
};

export default PostFooter;
